import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './Admin.module.css';
import UserList from './UserList';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import EventList from './EventList';
import { trackSvcEvent } from '../../analytics';

const Admin = () => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [users, setUsers] = useState([]);
    const [eventRegistrations, setEventRegistrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'users', 'events'
    const token = useSelector((state) => state.progression.token);

    useEffect(() => {
        // Vérifier si l'utilisateur est administrateur lorsque le token change
        if (token) {
            checkAdminStatus();
        }
    }, [token]);

    const checkAdminStatus = async () => {
        try {
            const response = await fetch('https://saviol-back-main.onrender.com/admin/check', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setIsAdmin(data.isAdmin);
                if (data.isAdmin) {
                    fetchUsers();
                    fetchEventRegistrations();
                }
            } else {
                setIsAdmin(false);
                setError('Vous n\'avez pas les droits d\'administration');
            }
        } catch (error) {
            console.error('Erreur lors de la vérification du statut admin:', error);
            setError('Erreur de connexion au serveur');
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://saviol-back-main.onrender.com/admin/users', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                setError('Erreur lors de la récupération des utilisateurs');
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const fetchEventRegistrations = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://saviol-back-main.onrender.com/admin/event-registrations', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setEventRegistrations(data);
            } else {
                setError('Erreur lors de la récupération des inscriptions aux événements');
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des inscriptions aux événements:', error);
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const updateUserVerification = async (userId, isVerified) => {
        try {
            setLoading(true); // Indiquer que l'opération est en cours

            const response = await fetch('https://saviol-back-main.onrender.com/admin/verify-user', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ userId, isVerified })
            });

            const responseData = await response.json();

            if (response.ok) {
                console.log("Mise à jour réussie:", responseData);

                if (isVerified === true) {
                    trackSvcEvent("approve_user", 1);
                } else if (isVerified === false) {
                    trackSvcEvent("approve_user", 0);
                }

                // Mettre à jour localement l'utilisateur modifié
                setUsers(prevUsers =>
                    prevUsers.map(user =>
                        user.id === userId
                            ? { ...user, is_verified: responseData.newStatus }
                            : user
                    )
                );

                // Afficher un message de succès temporaire
                setError('');
                const successMessage = isVerified === true
                    ? 'Utilisateur approuvé avec succès'
                    : isVerified === false
                        ? 'Utilisateur rejeté avec succès'
                        : 'Statut utilisateur mis à jour';

                setError(`✅ ${successMessage}`);
                setTimeout(() => setError(''), 3000); // Effacer le message après 3 secondes

                // Rafraîchir complètement la liste après un court délai
                setTimeout(() => {
                    fetchUsers();
                }, 1000);
            } else {
                console.error("Erreur lors de la mise à jour:", responseData);
                setError(`Erreur lors de la mise à jour du statut: ${responseData.error || 'Erreur inconnue'}`);
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du statut:', error);
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const updateEventRegistrationVerification = async (registrationId, isVerified) => {
        try {
            setLoading(true);

            console.log(`Envoi de la requête de mise à jour pour l'inscription ${registrationId} avec statut:`, isVerified);
            console.log(`Type de isVerified:`, typeof isVerified);

            const response = await fetch('https://saviol-back-main.onrender.com/admin/verify-event-registration', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ registrationId, isVerified })
            });

            const responseData = await response.json();
            console.log("Réponse du serveur:", responseData);

            if (response.ok) {
                console.log("Mise à jour de l'inscription à l'événement réussie:", responseData);
                console.log("Nouveau statut:", responseData.newStatus, "Type:", typeof responseData.newStatus);

                // Mettre à jour localement l'inscription modifiée
                setEventRegistrations(prevRegistrations =>
                    prevRegistrations.map(registration =>
                        registration.id === registrationId
                            ? { ...registration, event_verified: responseData.newStatus }
                            : registration
                    )
                );

                // Afficher un message de succès temporaire
                setError('');
                const successMessage = isVerified === true
                    ? 'Inscription à l\'événement approuvée avec succès'
                    : isVerified === false
                        ? 'Inscription à l\'événement rejetée avec succès'
                        : 'Statut d\'inscription mis à jour';

                setError(`✅ ${successMessage}`);
                setTimeout(() => setError(''), 3000);

                // Rafraîchir complètement la liste après un court délai
                setTimeout(() => {
                    fetchEventRegistrations();
                }, 1000);
            } else {
                console.error("Erreur lors de la mise à jour de l'inscription:", responseData);
                setError(`Erreur lors de la mise à jour du statut: ${responseData.error || 'Erreur inconnue'}`);
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du statut d\'inscription:', error);
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return <AdminLogin />;
    }

    if (!isAdmin) {
        return (
            <div className={styles.adminContainer}>
                <div className={styles.errorMessage}>
                    {error || "Vous n'avez pas les droits d'accès à cette page"}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.adminContainer}>
            <h1 className={styles.adminTitle}>Panneau d'Administration</h1>

            <div className={styles.adminTabs}>
                <button
                    className={`${styles.tabButton} ${activeTab === 'dashboard' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    Tableau de bord
                </button>
                <button
                    className={`${styles.tabButton} ${activeTab === 'users' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    Gestion des utilisateurs
                </button>
                <button
                    className={`${styles.tabButton} ${activeTab === 'events' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('events')}
                >
                    Inscriptions aux événements
                </button>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            {activeTab === 'dashboard' && (
                <AdminDashboard token={token} />
            )}

            {activeTab === 'users' && (
                loading ? (
                    <div className={styles.loadingSpinner}>Chargement...</div>
                ) : (
                    <UserList users={users} updateUserVerification={updateUserVerification} />
                )
            )}

            {activeTab === 'events' && (
                loading ? (
                    <div className={styles.loadingSpinner}>Chargement...</div>
                ) : (
                    <EventList
                        registrations={eventRegistrations}
                        updateEventRegistrationVerification={updateEventRegistrationVerification}
                    />
                )
            )}

            <button
                className={styles.refreshButton}
                onClick={() => {
                    if (activeTab === 'users') {
                        fetchUsers();
                    } else if (activeTab === 'events') {
                        fetchEventRegistrations();
                    }
                }}
            >
                Rafraîchir
            </button>
        </div>
    );
};

export default Admin; 